import { useRef, useState } from 'react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { StoredEmailViewer } from '@/components/admin/notification-management/StoredEmailViewer';
import { deliveryLabel } from '../../../../../supabase/functions/_shared/notification-activity-helpers';
import { useHistoryPage } from './useHistoryPage';
import { HistoryPager } from './HistoryPager';
import { historyStatus, historyTime, recordedContentHtml } from './historyFormat';

export function HistoryDetail(
  { loanId, messageId, onClose }: { loanId: string; messageId: string; onClose: () => void }
) {
  const state = useHistoryPage({ action: 'detail', loanId, messageId });
  const [preview, setPreview] = useState(false);
  const previewButton = useRef<HTMLButtonElement>(null);
  const item = state.data?.items[0];
  return (
    <section aria-label='Email details' className='min-w-0 space-y-4 rounded-lg border bg-muted/20 p-4'>
      <div className='flex flex-wrap items-center justify-between gap-2'>
        <h3 className='font-medium'>Email details</h3>
        <Button variant='outline' size='sm' onClick={onClose}>Close details</Button>
      </div>
      {state.loading && <p role='status'>Loading email details…</p>}
      {state.error && (
        <div role='alert'>
          <p>{state.error}</p>
          <Button variant='outline' onClick={state.refresh}>Retry details</Button>
        </div>
      )}
      {item && (
        <>
          <p className='break-words font-medium'>{item.subject || 'No subject'}</p>
          {item.isTest && <Badge variant='outline'>Test send</Badge>}
          <p className='text-sm'>{historyStatus(item)} · {historyTime(item.createdAt)}</p>
          <ul aria-label='Recipient outcomes' className='space-y-2 text-sm'>
            {item.recipients.map((recipient) => (
              <li key={recipient.email} className='flex flex-wrap justify-between gap-2 break-all'>
                <span>{recipient.role}: {recipient.email}</span>
                <span>{recipient.delivery ? deliveryLabel(recipient.delivery.state) : historyStatus(item)}</span>
              </li>
            ))}
          </ul>
          <Button
            ref={previewButton}
            variant='outline'
            disabled={!item.html}
            onClick={() => setPreview(true)}
          >
            View recorded content
          </Button>
          {item.contentKind === 'text_summary' && <p className='text-xs text-muted-foreground'>Only a text summary was recorded. The original email body is unavailable.</p>}
          {!item.html && <p className='text-xs text-muted-foreground'>The email body was not recorded.</p>}
          <h4 className='text-sm font-medium'>Recorded delivery and engagement events</h4>
          <p className='text-xs text-muted-foreground'>
            Opens and clicks can include automated activity. Dates use your device’s time zone.
          </p>
          {item.events.length
            ? (
              <ol className='space-y-2 text-xs'>
                {item.events.map((event) => (
                  <li key={event.svix_id} className='grid gap-1 border-b pb-2 sm:grid-cols-2'>
                    <span className='break-all'>
                      {event.event_type.replace('email.', '')} ·{' '}
                      {event.recipient_email || 'Recipient not recorded'}
                    </span>
                    <time dateTime={event.occurred_at}>{historyTime(event.occurred_at)}</time>
                  </li>
                ))}
              </ol>
            )
            : <p className='text-sm text-muted-foreground'>No provider events recorded.</p>}
          <HistoryPager state={state} label='Email event pages' />
          <StoredEmailViewer
            open={preview}
            onOpenChange={setPreview}
            blockExternalContent
            timestampLabel={item.direction === 'inbound' ? 'Received' : item.sentAt ? 'Sent' : 'Sent time'}
            missingTimestamp='Not recorded'
            onCloseAutoFocus={(event) => {
              event.preventDefault();
              previewButton.current?.focus();
            }}
            email={{
              id: item.id,
              subject: item.subject,
              html: recordedContentHtml(item.html ?? '', item.contentKind),
              toEmails: item.toEmails,
              ccEmails: item.ccEmails,
              sentAt: item.direction === 'inbound' ? item.createdAt : item.sentAt,
              status: historyStatus(item),
              context: { is_test: item.isTest }
            }}
          />
        </>
      )}
    </section>
  );
}
